import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';

export interface DocumentoGDB {
  id: string;
  user_id: string;
  nombre_documento: string;
  descripcion?: string;
  categoria: string;
  url_archivo: string;
  ruta_storage: string;
  nombre_archivo: string;
  tipo_archivo: string;
  tamano_archivo: number;
  metadata?: any;
  sistema_coordenadas?: string;
  proyeccion?: string;
  escala?: string;
  fecha_captura?: string;
  fuente_informacion?: string;
  resolucion_espacial?: string;
  area_cobertura?: string;
  created_at: string;
  updated_at: string;
}

const FORMATOS_PERMITIDOS = {
  gis: ['.mxd', '.gdb', '.shp', '.kml', '.kmz', '.mpk'],
  imagenes: ['.jpg', '.jpeg', '.png', '.tif', '.tiff'],
  documentos: ['.pdf', '.xlsx', '.xls', '.docx', '.doc'],
  cad: ['.dwg', '.dxf'],
  otros: ['.zip']
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

export const useDocumentosGDB = (categoriaFiltro?: string) => {
  const { user } = useAuth();
  const { cacheData, getCachedData, addPendingOperation, isOnline } = useOfflineStorage();
  const [documentos, setDocumentos] = useState<DocumentoGDB[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadDocumentos = async () => {
    if (!user) return;

    const cacheKey = `documentos_gdb_${user.id}_${categoriaFiltro || 'all'}`;

    try {
      setLoading(true);

      if (!isOnline) {
        const cached = await getCachedData('documentos', cacheKey);
        if (cached) {
          setDocumentos(cached);
        }
        return;
      }

      let query = supabase
        .from('documentos_gdb')
        .select('*')
        .order('created_at', { ascending: false });

      if (categoriaFiltro) {
        query = query.eq('categoria', categoriaFiltro);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      const docs = data || [];
      setDocumentos(docs);
      await cacheData('documentos', cacheKey, docs);
    } catch (error: any) {
      console.error('Error cargando documentos GDB:', error);
      
      const cached = await getCachedData('documentos', cacheKey);
      if (cached) {
        setDocumentos(cached);
        toast.error('Modo offline: mostrando metadata local');
      } else {
        toast.error('Error al cargar documentos');
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadDocumento = async (
    file: File,
    nombreDocumento: string,
    categoria: string,
    metadata: Partial<DocumentoGDB>
  ): Promise<boolean> => {
    if (!user) {
      toast.error('Debes iniciar sesión');
      return false;
    }

    // Validar tamaño
    if (file.size > MAX_FILE_SIZE) {
      toast.error('El archivo excede el tamaño máximo de 100 MB');
      return false;
    }

    // Validar formato
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    const formatoValido = Object.values(FORMATOS_PERMITIDOS)
      .flat()
      .includes(extension);

    if (!formatoValido) {
      toast.error('Formato de archivo no permitido');
      return false;
    }

    try {
      setUploading(true);

      // Sanitizar nombre de archivo: remover tildes, espacios y caracteres especiales
      const sanitizeFileName = (name: string): string => {
        return name
          .normalize('NFD') // Descomponer caracteres unicode
          .replace(/[\u0300-\u036f]/g, '') // Remover marcas diacríticas (tildes)
          .replace(/[^a-zA-Z0-9._-]/g, '_') // Reemplazar caracteres especiales con _
          .replace(/_{2,}/g, '_') // Reemplazar múltiples _ con uno solo
          .replace(/^_|_$/g, ''); // Remover _ al inicio y final
      };

      // Generar nombre único y sanitizado
      const timestamp = Date.now();
      const sanitizedFileName = sanitizeFileName(file.name);
      const nombreArchivo = `${timestamp}_${sanitizedFileName}`;
      const rutaStorage = `${user.id}/${nombreArchivo}`;

      // Subir archivo a Storage
      const { error: uploadError } = await supabase.storage
        .from('documentos-gdb')
        .upload(rutaStorage, file);

      if (uploadError) throw uploadError;

      // Obtener URL pública
      const { data: urlData } = supabase.storage
        .from('documentos-gdb')
        .getPublicUrl(rutaStorage);

      // Guardar metadata en base de datos
      const { error: dbError } = await supabase
        .from('documentos_gdb')
        .insert({
          user_id: user.id,
          nombre_documento: nombreDocumento,
          descripcion: metadata.descripcion,
          categoria,
          url_archivo: urlData.publicUrl,
          ruta_storage: rutaStorage,
          nombre_archivo: file.name,
          tipo_archivo: extension,
          tamano_archivo: file.size,
          sistema_coordenadas: metadata.sistema_coordenadas,
          proyeccion: metadata.proyeccion,
          escala: metadata.escala,
          fecha_captura: metadata.fecha_captura,
          fuente_informacion: metadata.fuente_informacion,
          resolucion_espacial: metadata.resolucion_espacial,
          area_cobertura: metadata.area_cobertura,
          metadata: metadata.metadata || {}
        });

      if (dbError) throw dbError;

      toast.success('Documento GDB subido correctamente');
      await loadDocumentos();
      return true;
    } catch (error: any) {
      console.error('Error subiendo documento GDB:', error);
      toast.error('Error al subir el documento');
      return false;
    } finally {
      setUploading(false);
    }
  };

  const deleteDocumento = async (documento: DocumentoGDB): Promise<boolean> => {
    if (!user) return false;

    try {
      // Eliminar de Storage
      const { error: storageError } = await supabase.storage
        .from('documentos-gdb')
        .remove([documento.ruta_storage]);

      if (storageError) throw storageError;

      // Eliminar de base de datos
      const { error: dbError } = await supabase
        .from('documentos_gdb')
        .delete()
        .eq('id', documento.id);

      if (dbError) throw dbError;

      toast.success('Documento eliminado correctamente');
      await loadDocumentos();
      return true;
    } catch (error: any) {
      console.error('Error eliminando documento GDB:', error);
      toast.error('Error al eliminar el documento');
      return false;
    }
  };

  const updateDocumento = async (
    id: string,
    updates: Partial<DocumentoGDB>
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      if (!isOnline) {
        await addPendingOperation('update', 'documentos_gdb', {
          id,
          ...updates,
        });

        setDocumentos(prev => 
          prev.map(d => d.id === id ? { ...d, ...updates } : d)
        );

        toast.success('Cambios guardados offline');
        return true;
      }

      const { error } = await supabase
        .from('documentos_gdb')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast.success('Documento actualizado correctamente');
      await loadDocumentos();
      return true;
    } catch (error: any) {
      console.error('Error actualizando documento GDB:', error);
      toast.error('Error al actualizar el documento');
      return false;
    }
  };

  const getEstadisticas = () => {
    const stats = {
      total: documentos.length,
      porCategoria: {} as Record<string, number>,
      porTipo: {} as Record<string, number>,
      tamanoTotal: 0
    };

    documentos.forEach(doc => {
      // Por categoría
      stats.porCategoria[doc.categoria] = (stats.porCategoria[doc.categoria] || 0) + 1;
      
      // Por tipo
      stats.porTipo[doc.tipo_archivo] = (stats.porTipo[doc.tipo_archivo] || 0) + 1;
      
      // Tamaño total
      stats.tamanoTotal += doc.tamano_archivo;
    });

    return stats;
  };

  useEffect(() => {
    loadDocumentos();

    // Suscripción en tiempo real
    const channel = supabase
      .channel('documentos-gdb-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'documentos_gdb'
        },
        () => {
          loadDocumentos();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, categoriaFiltro]);

  return {
    documentos,
    loading,
    uploading,
    uploadDocumento,
    deleteDocumento,
    updateDocumento,
    getEstadisticas,
    loadDocumentos
  };
};
